import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconChevron } from '@groupelacentrale/contact-storybook/lib/icons';
import { ResizeObserver } from '@juggle/resize-observer';
import './CollapsibleContainer.scss';

interface Props {
  arrowSize?: number;
  className?: string;
  defaultOpen?: boolean;
  id?: string;
  onClose?: (id?: string) => void | undefined;
  onOpen?: (id?: string) => void | undefined;
  open?: boolean;
  title: React.ReactNode;
  children: React.ReactNode;
}

const Container: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
  { arrowSize, children, className, defaultOpen, id, onClose, onOpen, open, title },
  containerRef,
) => {
  const [expanded, setExpanded] = useState(defaultOpen !== undefined ? defaultOpen : true);
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const showExpanded = open !== undefined ? open : expanded;

  useEffect(() => {
    const observer = new ResizeObserver(function (entries) {
      setHeight(entries[0].contentRect.height);
    });

    if (ref && ref.current) {
      setHeight(ref.current.getBoundingClientRect().height);
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  const toggleExpand = useCallback(() => {
    if (open !== undefined) {
      open ? typeof onClose === 'function' && onClose(id) : typeof onOpen === 'function' && onOpen(id);
    } else {
      setExpanded(isExpanded => !isExpanded);
    }
  }, [id, open, onOpen, onClose]);

  return (
    <div ref={containerRef} className={`collapsible-container ${className || ''}`} id={id}>
      <div className="title-wrapper" onClick={toggleExpand}>
        <div className="title">{title}</div>
        <IconChevron width={arrowSize || 30} rotation={showExpanded ? 90 : 270} />
      </div>
      <div className="content-wrapper" style={{ height: showExpanded ? height : 0 }}>
        <div ref={ref}>{children}</div>
      </div>
    </div>
  );
};

export const CollapsibleContainer = React.forwardRef(Container);
